import React, { useEffect, useState } from 'react';
import {
  Button,
  DataTableFilter,
  ExportToExcelIcon,
  RenderIf,
  StyledAggregateContainer,
  StyledPaginationBar,
  StyledTable,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHeader,
  StyledTextChip,
  TableFilterItem,
  TableHeaderLabel,
} from '@lib/ui-components';
import { extractValue, longNumericToString } from '@lib/common-sdk';
import axios, { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import styles from '../data-table/styles.module.css';
import { DataSearchField } from './table-search-field/data-search-field';
import { ArrowDownward } from '@mui/icons-material';
import Paper from '@mui/material/Paper';
import { StyledBooleanTrueSign } from './StyledBooleanTrueSign';
import { StyledBooleanFalseSign } from './StyledBooleanFalseSign';
import { format, isValid, parseISO } from 'date-fns';
import LinearProgress from '@mui/material/LinearProgress';
import { Checkbox, TablePagination, Tooltip } from '@mui/material';

import { DataTableColumn, DataTableRowOptionItem, SelectableOptions } from './data-table';
import { toTranslations, transformToNumberString } from './utils/data-table-utils';

export interface ServerPaginatedTableProps {
  fetchUrl?: string;
  fetchPostUrl?: string;
  fetchFilters?: any;
  responsePath?: {
    content?: string;
    totalElements?: string;
  };

  columns: DataTableColumn[];
  rowOptions?: DataTableRowOptionItem[];
  refreshKey: number;

  defaultPageSize?: number;

  initialSort?: SortingInfo;

  selectable?: boolean;
  selectableOptions?: SelectableOptions;

  onRowClicked?: (row: any) => void;
  onTableLoaded?: (rows: any, totalCount: number) => void;

  rowOptionsMinWidth?: string;
  columnsMinWidth?: string;

  excel?: boolean;
  defaultTableFilters?: Array<TableFilterItem>;
  onDefaultTableFiltersUpdated?: (filters: Array<TableFilterItem>) => void;
  contentMaxHeightAsAlmostOneScreen?: boolean;
  customRowStyleIfTrue?: {
    condition: (row: any) => boolean;
    style: React.CSSProperties;
  };
  noFilters?: boolean;
  rowsPerPageOptions?: number[];
}

interface SortingInfo {
  columnIndex: number;
  ascending: boolean;
}

const LinearIndeterminate = () => {
  return (
    <div className={styles['progressContainer']}>
      <LinearProgress />
    </div>
  );
};

export const ServerPaginatedTable = (props: ServerPaginatedTableProps) => {
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pending, setPending] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(props.defaultPageSize || 50);

  const [sortInfo, setSortInfo] = useState<SortingInfo>(
    props.initialSort || {
      columnIndex: 0,
      ascending: true,
    },
  );

  const [filterText, setFilterText] = useState('');
  const [activeFilters, setActiveFilters] = useState<TableFilterItem[]>([]);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [disableSelection, setDisableSelection] = useState(false);

  const [exportButtonClicked, setExportButtonClicked] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { t, i18n } = useTranslation();
  const dateFormat = i18n.language === 'pl' ? 'dd.MM.yyyy' : 'MM/dd/yyyy';
  const dateYearMonthFormat = i18n.language === 'pl' ? 'MM.yyyy' : 'MM/yyyy';
  const dateTimeFormat = i18n.language === 'pl' ? 'dd.MM.yyyy HH:mm:ss' : 'MM/dd/yyyy HH:mm:ss';

  useEffect(() => {
    let isCancelled = false;
    setPending(true);

    const sortColumn = props.columns[sortInfo.columnIndex].key;
    const sortDirection = sortInfo.ascending ? 'asc' : 'desc';
    const sortParam = `${sortColumn},${sortDirection}`;

    const filterParams = activeFilters.reduce((acc, filter) => {
      if (filter.value !== null && filter.value !== undefined) {
        if (filter.column.type === 'date' || filter.column.type === 'date-year-month' || filter.column.type === 'date-time') {
          const [fromDate, toDate] = filter.value as [Date | null, Date | null];
          if (fromDate) {
            acc[`${filter.column.key}From`] = formatDateForApi(fromDate, filter.column.type);
          }
          if (toDate) {
            acc[`${filter.column.key}To`] = formatDateForApi(toDate, filter.column.type, true);
          }
        } else {
          acc[filter.column.key] = filter.value;
        }
      }
      return acc;
    }, {} as Record<string, any>);

    const params = {
      ...props.fetchFilters,
      ...filterParams,
      page: page,
      size: rowsPerPage,
      sort: sortParam,
      search: filterText || undefined,
    };

    if (props.fetchUrl) {
      axios
        .get(props.fetchUrl, { params })
        .then((response: AxiosResponse) => {
          if (!isCancelled) {
            const responseData = response.data;
            const contentPath = props.responsePath?.content;
            const content = (contentPath ? extractValue(responseData, contentPath) : responseData.content) || [];

            const totalElementsPath = props.responsePath?.totalElements;
            const totalElements = totalElementsPath ? extractValue(responseData, totalElementsPath) : responseData.totalElements || 0;

            setRows(content);
            setTotalCount(totalElements);
            if (props.onTableLoaded) {
              props.onTableLoaded(content, totalElements);
            }
            setPending(false);
          }
        })
        .catch((error) => {
          if (!isCancelled) {
            console.error('Error fetching data:', error);
            setPending(false);
          }
        });
    } else if (props.fetchPostUrl) {
      axios
        .post(props.fetchPostUrl, {
          ...params,
          filters: props.fetchFilters,
        })
        .then((response: AxiosResponse) => {
          if (!isCancelled) {
            const responseData = response.data;
            const contentPath = props.responsePath?.content;
            const content = (contentPath ? extractValue(responseData, contentPath) : responseData.content) || [];

            const totalElementsPath = props.responsePath?.totalElements;
            const totalElements = totalElementsPath ? extractValue(responseData, totalElementsPath) : responseData.totalElements || 0;

            setRows(content);
            setTotalCount(totalElements);
            if (props.onTableLoaded) {
              props.onTableLoaded(content, totalElements);
            }
            setPending(false);
          }
        })
        .catch((error) => {
          if (!isCancelled) {
            console.error('Error fetching data:', error);
            setPending(false);
          }
        });
    } else {
      throw Error('ServerPaginatedTable requires a fetchUrl or fetchPostUrl');
    }

    return () => {
      isCancelled = true;
    };
  }, [props.refreshKey, props.fetchFilters, page, rowsPerPage, sortInfo, filterText, activeFilters]);

  useEffect(() => {
    if (props.selectable && props.selectableOptions?.onSelectedRowsChange) {
      const selectedRows = rows.filter((row) => {
        const comparatorField = props.selectableOptions?.comparatorField || 'id';
        return selectedRowIds.includes(row[comparatorField]?.toString());
      });
      props.selectableOptions.onSelectedRowsChange(selectedRows);
    }
  }, [selectedRowIds, rows]);

  useEffect(() => {
    setSelectedRowIds([]);
    setDisableSelection(false);
  }, [props.refreshKey]);

  const formatDateForApi = (date: Date, type: string, isEndDate = false) => {
    if (type === 'date') {
      return format(date, 'yyyy-MM-dd');
    } else if (type === 'date-year-month') {
      return format(date, 'yyyy-MM');
    } else if (type === 'date-time') {
      if (isEndDate) {
        date.setHours(23, 59, 59, 999);
      }
      return format(date, 'yyyy-MM-dd\'T\'HH:mm:ss');
    }
    return '';
  };

  const tableHeader = React.useMemo(() => {
    const header = props.columns.map((column, index) => {
      if (column.hidden) {
        return <StyledTableHeader key={`h${index}`} style={{ display: 'none' }} />;
      }
      return (
        <StyledTableHeader
          key={`h${index}`}
          style={{
            minWidth: column.minWidth,
            maxWidth: column.maxWidth,
            cursor: 'pointer',
            whiteSpace: column.maxWidth ? 'break-spaces' : 'nowrap',
          }}
          onClick={() => {
            const newSortInfo = {
              columnIndex: index,
              ascending: index === sortInfo.columnIndex ? !sortInfo.ascending : true,
            };
            setSortInfo(newSortInfo);
            setPage(0);
          }}
        >
          <TableHeaderLabel align={column.align}>
            <span style={{ textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: column.label }}></span>
            {!props.noFilters && sortInfo.columnIndex === index && (
              <ArrowDownward
                style={{
                  width: '16px',
                  transform: sortInfo.ascending ? 'rotate(180deg)' : '',
                }}
              />
            )}
          </TableHeaderLabel>
        </StyledTableHeader>
      );
    });

    if (props.rowOptions) {
      header.push(
        <StyledTableHeader
          key='rowOptionsHeader'
          style={{
            right: 0,
            position: 'sticky',
            width: '1px',
            zIndex: 1,
          }}
        >
          <TableHeaderLabel align={'left'}>
            <span style={{ textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: t('options') }}></span>
          </TableHeaderLabel>
        </StyledTableHeader>,
      );
    }

    if (props.selectable) {
      const comparatorField = props.selectableOptions?.comparatorField || 'id';
      const allRowsSelected = rows.length > 0 && rows.every((row) => selectedRowIds.includes(row[comparatorField]?.toString()));
      const someRowsSelected = selectedRowIds.length > 0 && !allRowsSelected;
      const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
          const newSelectedIds = [...selectedRowIds];
          rows.forEach((row) => {
            const rowId = row[comparatorField]?.toString();
            if (rowId && !newSelectedIds.includes(rowId)) {
              newSelectedIds.push(rowId);
            }
          });
          setSelectedRowIds(newSelectedIds);
        } else {
          const visibleRowIds = rows.map((row) => row[comparatorField]?.toString());
          setSelectedRowIds(selectedRowIds.filter((id) => !visibleRowIds.includes(id)));
        }
      };

      header.unshift(
        <StyledTableHeader
          key='rowSelectHeader'
          style={{
            right: 0,
            position: 'sticky',
            width: '1px',
            zIndex: 1,
          }}
        >
          <Checkbox indeterminate={someRowsSelected} checked={allRowsSelected && rows.length > 0} disabled={props.selectableOptions?.singleSelect} onChange={handleSelectAll} />
        </StyledTableHeader>,
      );
    }

    return header;
  }, [props.columns, props.rowOptions, sortInfo, selectedRowIds, rows, disableSelection]);

  const renderCellContent = (row: any, column: DataTableColumn, onlyStringAsResult = false, dontFormatNumbers = false) => {
    const value = extractValue(row, column.key);

    const customValueMapping = column.customValueMappings?.find((mapping) => mapping.value === value);

    if (customValueMapping) {
      if (onlyStringAsResult) {
        return typeof customValueMapping.label === 'string' ? customValueMapping.label : customValueMapping.stringLabel ?? '';
      }
      return customValueMapping.label;
    }

    switch (column.type) {
      case 'boolean': {
        const valueIsTrue = column.expressionForBooleanType ? column.expressionForBooleanType(row) : value === true || value === 'true';

        if (onlyStringAsResult) return valueIsTrue ? t('yes') : t('no');
        return valueIsTrue ? <StyledBooleanTrueSign /> : <StyledBooleanFalseSign />;
      }

      case 'numeric':
        if (dontFormatNumbers) return value;
        if (value == null) return '';
        if (column.numericPrecision != null) {
          return transformToNumberString(value, column.numericPrecision);
        }
        return value.toString().replace('.', ',');

      case 'long-numeric':
        if (value == null) return '';
        return longNumericToString(value, column.numericPrecision, ',');

      case 'date':
        if (value != null && value !== '') {
          const date = parseISO(value);
          if (isValid(date)) {
            return format(date, column.dateFormat || dateFormat);
          }
        }
        return '';

      case 'date-year-month':
        if (value != null && value !== '') {
          const date = parseISO(value);
          if (isValid(date)) {
            return format(date, dateYearMonthFormat);
          }
        }
        return '';

      case 'date-time':
        if (value != null && value !== '') {
          const date = parseISO(value);
          if (isValid(date)) {
            return format(date, dateTimeFormat);
          }
        }
        return '';

      case 'enum':
        if (column.enumValues != null && value != null) {
          const enumValue = column.enumValues.find((item) => item.value === value);
          if (enumValue) {
            return enumValue.label;
          }
        }
        return '';

      case 'text-chip':
        if (value == null) return '';
        if (Array.isArray(value)) {
          if (onlyStringAsResult) {
            return value.join(', ');
          }
          return (
            <StyledAggregateContainer>
              {value.map((item, idx) => (
                <StyledTextChip key={idx}>{item.toString()}</StyledTextChip>
              ))}
            </StyledAggregateContainer>
          );
        }
        return <StyledTextChip>{value.toString()}</StyledTextChip>;

      case 'string-array-boolean': {
        const booleanArrayValue = Array.isArray(value) && value.includes(column.stringArrayValue ?? '');
        return booleanArrayValue ? t('yes') : t('no');
      }

      case 'string-array-multiline': {
        const raw = Array.isArray(value) ? value : [];
        const sortedRaw = [...raw].sort((a, b) => {
          if (typeof a === 'string' || typeof b === 'string') return 0;
          const aCreated = a?.createdAt ?? '';
          const bCreated = b?.createdAt ?? '';
          if (aCreated !== bCreated) return aCreated.localeCompare(bCreated);
          return (a?.uuid ?? '').localeCompare(b?.uuid ?? '');
        });
        const lines: string[] = sortedRaw
          .map((item) => (typeof item === 'string' ? item : item?.value))
          .filter((v: any) => v != null && v !== '');
        if (lines.length === 0) return '';
        if (onlyStringAsResult) {
          return lines.join('\n');
        }
        const first = lines[0];
        const tooltipContent = (
          <span style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</span>
        );
        return (
          <Tooltip title={tooltipContent} arrow placement='top'>
            <span style={{ display: 'inline-block', maxWidth: '20rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              {first}
              {lines.length > 1 ? ` (+${lines.length - 1})` : ''}
            </span>
          </Tooltip>
        );
      }

      case 'string-array-boolean-aggregate': {
        if (onlyStringAsResult) {
          return (
            column.enumValues
              ?.map((enumVal) => {
                return Array.isArray(value) && value.includes(enumVal.value) ? enumVal.label : '';
              })
              .filter((val) => val !== '')
              .join(', ') ?? ''
          );
        }

        const aggregateValues = column.enumValues?.map((enumVal, idx) => (
          <StyledTextChip key={`c${idx}`} normal={!Array.isArray(value) || !value.includes(enumVal.value)}>
            {enumVal.label}
          </StyledTextChip>
        ));

        return <StyledAggregateContainer>{aggregateValues}</StyledAggregateContainer>;
      }

      case 'object': {
        if (!value) return '';
        let result = '';
        for (const key in value) {
          if (Object.hasOwn(value, key)) {
            const val = value[key];
            if (val != null && val !== '') {
              const translatedKey = t(key as any);
              const translatedVal = t(val as any);
              result += `${translatedKey}: ${translatedVal}\n`;
            }
          }
        }
        return result;
      }

      default:
        return value != null ? value.toString() : '';
    }
  };
  const tableRows = React.useMemo(() => {
    return rows.map((row, rowIndex) => {
      const cells = props.columns.map((column, columnIndex) => {
        if (column.hidden) {
          return <StyledTableCell key={`cell-${rowIndex}-${columnIndex}`} style={{ display: 'none' }} />;
        }

        if (column.showEmptyIfTrue && column.showEmptyIfTrue(row)) {
          return <StyledTableCell key={`cell-${rowIndex}-${columnIndex}`} />;
        }

        const cellProps: any = {
          key: `cell-${rowIndex}-${columnIndex}`,
          align: column.align,
          style: { whiteSpace: 'break-spaces' },
        };

        if (props.onRowClicked) {
          cellProps.onClick = () => props.onRowClicked!(row);
          cellProps.style.cursor = 'pointer';
        }

        if (column.color) {
          cellProps.style.color = column.color;
        }
        if (column.colorOnCondition) {
          cellProps.style.color = column.colorOnCondition(row);
        }

        if (column.singleLine) {
          cellProps.style.whiteSpace = 'nowrap';
          cellProps.style.textOverflow = 'ellipsis';
          cellProps.style.overflow = 'hidden';
          cellProps.style.maxWidth = (column.singleLine.maxCellWidthInRem || 50) + 'rem';
        }

        if (column.renderer) {
          return <StyledTableCell {...cellProps}>{column.renderer(row)}</StyledTableCell>;
        }

        if (column.additionalElement) {
          return (
            <StyledTableCell {...cellProps}>
              {renderCellContent(row, column)} {column.additionalElement(row)}
            </StyledTableCell>
          );
        }

        if (column.joinedColumns && column.joinedColumns.length > 0) {
          const mainContent = renderCellContent(row, column);
          const joinedContent = column.joinedColumns
            .map((joinedColumn) => renderCellContent(row, joinedColumn))
            .filter((content) => content !== undefined && content !== '')
            .join(' ');

          const combinedContent = `${mainContent} ${joinedContent}`;

          return <StyledTableCell {...cellProps}>{combinedContent}</StyledTableCell>;
        }

        return <StyledTableCell {...cellProps}>{renderCellContent(row, column)}</StyledTableCell>;
      });

      if (props.rowOptions) {
        const options = props.rowOptions
          .filter((option) => (option.isVisible ? option.isVisible(row) : true))
          .map((option, optionIndex) => <div key={`option-${rowIndex}-${optionIndex}`}>{option.renderer(row)}</div>);

        cells.push(
          <StyledTableCell
            key={`options-${rowIndex}`}
            style={{
              right: 0,
              position: 'sticky',
              backgroundColor: '#ffffff',
            }}
          >
            <div className={styles['rowOptionsContainer']}>{options}</div>
          </StyledTableCell>,
        );
      }

      if (props.selectable) {
        const comparatorField = props.selectableOptions?.comparatorField || 'id';
        const rowId = row[comparatorField]?.toString();
        const isSelected = selectedRowIds.includes(rowId);

        const handleRowSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.checked) {
            if (props.selectableOptions?.singleSelect) {
              setSelectedRowIds([rowId]);
            } else {
              setSelectedRowIds([...selectedRowIds, rowId]);
            }
          } else {
            setSelectedRowIds(selectedRowIds.filter((id) => id !== rowId));
          }
        };

        cells.unshift(
          <StyledTableCell
            key={`select-${rowIndex}`}
            style={{
              right: 0,
              position: 'sticky',
              backgroundColor: '#ffffff',
            }}
          >
            <Checkbox checked={isSelected} onChange={handleRowSelection} disabled={disableSelection && !isSelected} />
          </StyledTableCell>,
        );
      }

      if (props.customRowStyleIfTrue?.condition(row)) {
        return (
          <tr key={`row-${rowIndex}`} style={props.customRowStyleIfTrue.style}>
            {cells}
          </tr>
        );
      }

      return <tr key={`row-${rowIndex}`}>{cells}</tr>;
    });
  }, [rows, props.columns, props.rowOptions, selectedRowIds, disableSelection]);

  const formatExportData = () => {
    return {
      sheets: [
        {
          rows: rows.map((row) => {
            const formattedRow: Record<string, any> = {};
            props.columns.forEach((column) => {
              formattedRow[column.key] = renderCellContent(row, column, true, true);
            });
            return formattedRow;
          }),
          parameters: {
            columnDefinitions: props.columns.map((column) => ({
              key: column.key,
              label: column.label,
              translations: toTranslations(column),
              numericPrecision: column.numericPrecision,
              hidden: column.hidden || false,
            })),
          },
        },
      ],
    };
  };

  useEffect(() => {
    const exportToExcel = async () => {
      try {
        setIsExporting(true);
        let exportData;
        if (totalCount <= rowsPerPage) {
          exportData = formatExportData();
        } else {
          const exportSize = Math.min(totalCount);
          const sortColumn = props.columns[sortInfo.columnIndex].key;
          const sortDirection = sortInfo.ascending ? 'asc' : 'desc';
          const sortParam = `${sortColumn},${sortDirection}`;
          const response = await axios({
            method: 'get',
            url: props.fetchUrl,
            params: {
              ...props.fetchFilters,
              page: 0,
              size: exportSize,
              sort: sortParam,
            },
          });

          const responseData = response.data;
          const contentPath = props.responsePath?.content;
          const allData = (contentPath ? extractValue(responseData, contentPath) : responseData.content) || [];

          exportData = {
            sheets: [
              {
                rows: allData.map((row: any) => {
                  const formattedRow: Record<string, any> = {};
                  props.columns.forEach((column) => {
                    formattedRow[column.key] = renderCellContent(row, column, true, true);
                  });
                  return formattedRow;
                }),
                parameters: {
                  columnDefinitions: props.columns.map((column) => ({
                    key: column.key,
                    label: column.label,
                    translations: toTranslations(column),
                    numericPrecision: column.numericPrecision,
                    hidden: column.hidden || false,
                  })),
                },
              },
            ],
          };
        }

        const response = await axios({
          method: 'post',
          data: exportData,
          url: '/api/excel',
          responseType: 'blob',
        });

        const contentDispositionHeader = response.headers['content-disposition'];

        let fileName = 'export.xlsx';
        if (contentDispositionHeader) {
          const fileNameMatch = contentDispositionHeader.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/);
          if (fileNameMatch && fileNameMatch.length > 1) {
            fileName = decodeURIComponent(fileNameMatch[1]);
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error exporting to Excel:', error);
      } finally {
        setIsExporting(false);
        setExportButtonClicked(false);
      }
    };

    if (exportButtonClicked) {
      exportToExcel();
    }
  }, [exportButtonClicked]);
  const handleFilterChange = (filters: TableFilterItem[]) => {
    setActiveFilters(filters);
    setPage(0);

    if (props.onDefaultTableFiltersUpdated) {
      props.onDefaultTableFiltersUpdated(filters);
    }
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper className={styles['paperContainer']}>
      <div className={styles['dataTableContainerWrapper']}>
        <div className={props.contentMaxHeightAsAlmostOneScreen ? styles['dataTableContainerWithMaxHeight'] : styles['dataTableContainer']}>
          <RenderIf true={props.excel}>
            <div className={styles['tableToolbar']}>
              <Button
                label={t('exportToExcel')}
                startIcon={<ExportToExcelIcon />}
                onClick={() => setExportButtonClicked(true)}
                isDisabled={isExporting || rows.length === 0 || totalCount === 0}
              />
            </div>
          </RenderIf>

          <RenderIf false={props.noFilters}>
            <div className={styles['tableToolbar']}>
              <DataTableFilter columns={props.columns} onFiltersChange={handleFilterChange} initialFilters={props.defaultTableFilters} />
              <div>
                <DataSearchField
                  onFiltersChange={(filter) => {
                    setFilterText(filter);
                    setPage(0);
                  }}
                />
              </div>
            </div>
          </RenderIf>

          <StyledTableContainer>
            <StyledTable>
              <thead>{tableHeader}</thead>
              <tbody>{tableRows}</tbody>
            </StyledTable>
            {pending && <LinearIndeterminate />}
          </StyledTableContainer>

          {!pending && (
            <StyledPaginationBar>
              <table>
                <tfoot>
                <tr>
                  <TablePagination
                    count={totalCount}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={props.rowsPerPageOptions || [10, 25, 50, 100]}
                    showFirstButton={true}
                    showLastButton={true}
                    labelRowsPerPage={t('SmartDataTable.numberOfItems')}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('SmartDataTable.of')} ${count}`}
                  />
                </tr>
                </tfoot>
              </table>
            </StyledPaginationBar>
          )}
        </div>
      </div>
    </Paper>
  );
};
